// Generated by the Exylon v4 core contract pipeline. Do not edit.
// Source: backend/src/platform/calendar/madrid.ts, copied verbatim.
/**
 * The one restaurant calendar of the Backend: Europe/Madrid
 * (`docs/decisions/2026-09-28-spain-only-eur-and-madrid.md`).
 *
 * Instants stay instants (ISO UTC on the wire). Every calendar question — the
 * business day of an instant, «hoy», the bounds of a local day or month — is
 * answered here, in the fixed zone. A Madrid day lasts 23 or 25 hours at the
 * clock changes, so a calendar day is never `86_400_000` milliseconds: day
 * arithmetic works on date-only values and bounds come from the zone.
 *
 * This leaf imports nothing, so the API and the worker programs each load it
 * without sharing any other module. The SQL counterpart is the literal
 * `'Europe/Madrid'` in `AT TIME ZONE` expressions.
 */
export const MADRID_TIME_ZONE = 'Europe/Madrid' as const

export class MadridCalendarError extends Error {
  constructor(readonly code: 'INSTANT_INVALID' | 'DATE_INVALID' | 'RANGE_INVALID') {
    super(
      code === 'INSTANT_INVALID'
        ? 'A valid instant is required'
        : code === 'DATE_INVALID'
          ? 'A calendar date YYYY-MM-DD is required'
          : 'A valid calendar range is required'
    )
    this.name = 'MadridCalendarError'
  }
}

export type MadridLocalTime = Readonly<{
  /** The instant, ISO UTC. */
  instant: string
  /** The Madrid calendar date, YYYY-MM-DD. */
  localDate: string
  /** Minutes since the Madrid midnight on the wall clock (0–1439). */
  localMinutes: number
  /** 0 = Sunday … 6 = Saturday. */
  weekday: number
  /** 1 = Monday … 7 = Sunday. */
  isoWeekday: number
}>

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/
const DAY_MS = 86_400_000
const WEEKDAY: Readonly<Record<string, number>> = Object.freeze({ Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 })

const FORMAT = new Intl.DateTimeFormat('en-US-u-ca-iso8601-nu-latn', {
  timeZone: MADRID_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
  weekday: 'short'
})

const instantOf = (value: string | Date): Date => {
  const instant = value instanceof Date ? new Date(value.getTime()) : typeof value === 'string' ? new Date(value) : new Date(Number.NaN)
  if (!Number.isFinite(instant.getTime())) throw new MadridCalendarError('INSTANT_INVALID')
  return instant
}

type WallClock = Readonly<{ year: number; month: number; day: number; hour: number; minute: number; second: number; weekday: number }>

const wallClock = (instant: Date): WallClock => {
  const parts: Record<string, string> = {}
  for (const part of FORMAT.formatToParts(instant)) if (part.type !== 'literal') parts[part.type] = part.value
  const weekday = WEEKDAY[parts.weekday ?? '']
  if (weekday === undefined) throw new MadridCalendarError('INSTANT_INVALID')
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
    weekday
  }
}

const pad = (value: number, width = 2): string => String(value).padStart(width, '0')
const dateText = (year: number, month: number, day: number): string => `${pad(year, 4)}-${pad(month)}-${pad(day)}`

/** Milliseconds the Madrid wall clock is ahead of UTC at this instant. */
const offsetAt = (epochMs: number): number => {
  const wall = wallClock(new Date(epochMs))
  const wallAsUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second)
  return wallAsUtc - (epochMs - (((epochMs % 1000) + 1000) % 1000))
}

/** The Madrid wall-clock projection of one instant. */
export const madridLocalTime = (value: string | Date): MadridLocalTime => {
  const instant = instantOf(value)
  const wall = wallClock(instant)
  return Object.freeze({
    instant: instant.toISOString(),
    localDate: dateText(wall.year, wall.month, wall.day),
    localMinutes: wall.hour * 60 + wall.minute,
    weekday: wall.weekday,
    isoWeekday: wall.weekday === 0 ? 7 : wall.weekday
  })
}

/** The Madrid calendar date (YYYY-MM-DD) of an instant. */
export const madridDate = (value: string | Date): string => madridLocalTime(value).localDate

/** Today's Madrid calendar date. */
export const madridToday = (now: Date = new Date()): string => madridDate(now)

/** True for a real calendar date: the shape and an exact round trip (no month 13, no 30 February). */
export const isCalendarDate = (value: unknown): value is string => {
  if (typeof value !== 'string' || !CALENDAR_DATE.test(value)) return false
  const parsed = new Date(`${value}T00:00:00.000Z`)
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

export const assertCalendarDate = (value: unknown): string => {
  if (!isCalendarDate(value)) throw new MadridCalendarError('DATE_INVALID')
  return value
}

/** Calendar arithmetic on a date-only value (never on an instant). */
export const addCalendarDays = (date: string, days: number): string => {
  assertCalendarDate(date)
  if (!Number.isSafeInteger(days)) throw new MadridCalendarError('RANGE_INVALID')
  return new Date(Date.parse(`${date}T00:00:00.000Z`) + days * DAY_MS).toISOString().slice(0, 10)
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export const calendarDaysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${assertCalendarDate(to)}T00:00:00.000Z`) - Date.parse(`${assertCalendarDate(from)}T00:00:00.000Z`)) / DAY_MS)

/** ISO weekday of a calendar date: 1 = Monday … 7 = Sunday. */
export const isoWeekdayOfDate = (date: string): number => {
  const weekday = new Date(`${assertCalendarDate(date)}T00:00:00.000Z`).getUTCDay()
  return weekday === 0 ? 7 : weekday
}

const SPANISH_WEEKDAYS = Object.freeze(['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'])

/** A calendar date as people say it in Spanish: the weekday and the day of the month («jueves 8»). */
export const spanishWeekdayDay = (date: string): string => `${SPANISH_WEEKDAYS[isoWeekdayOfDate(date) - 1]} ${Number(date.slice(8, 10))}`

/** The first date from `from` on (it included) whose ISO weekday is one of `weekdays`; `from` itself when none is given. */
export const firstDateOnWeekdays = (from: string, weekdays: readonly number[]): string => {
  for (let offset = 0; offset < 7; offset += 1) {
    const date = addCalendarDays(from, offset)
    if (weekdays.includes(isoWeekdayOfDate(date))) return date
  }
  return assertCalendarDate(from)
}

/** The first day of the calendar month of `date`, shifted by `months`. */
export const firstDayOfMonth = (date: string, months = 0): string => {
  assertCalendarDate(date)
  if (!Number.isSafeInteger(months)) throw new MadridCalendarError('RANGE_INVALID')
  const year = Number(date.slice(0, 4))
  const month = Number(date.slice(5, 7)) - 1 + months
  const shifted = new Date(Date.UTC(year, month, 1))
  return shifted.toISOString().slice(0, 10)
}

/** The instant of 00:00 in Madrid on a calendar date (Madrid midnight always exists). */
export const madridDayStart = (date: string): string => {
  assertCalendarDate(date)
  const midnightAsUtc = Date.parse(`${date}T00:00:00.000Z`)
  let candidate = midnightAsUtc - offsetAt(midnightAsUtc)
  candidate = midnightAsUtc - offsetAt(candidate)
  return new Date(candidate).toISOString()
}

/**
 * The instant the Madrid wall clock shows `minutes` past midnight on a calendar date (07:00 = 420). The
 * offset is taken at the target instant, so a clock change earlier that day is accounted for.
 */
export const madridWallClockInstant = (date: string, minutes: number): string => {
  assertCalendarDate(date)
  if (!Number.isSafeInteger(minutes) || minutes < 0 || minutes >= 24 * 60) throw new MadridCalendarError('RANGE_INVALID')
  const wallAsUtc = Date.parse(`${date}T00:00:00.000Z`) + minutes * 60_000
  let candidate = wallAsUtc - offsetAt(wallAsUtc)
  candidate = wallAsUtc - offsetAt(candidate)
  return new Date(candidate).toISOString()
}

export type MadridInterval = Readonly<{ from_inclusive: string; until_exclusive: string }>

/** The half-open instant interval of one Madrid day: 23, 24 or 25 hours. */
export const madridDayBounds = (date: string): MadridInterval =>
  Object.freeze({ from_inclusive: madridDayStart(date), until_exclusive: madridDayStart(addCalendarDays(date, 1)) })

/** The half-open instant interval of the Madrid calendar dates `from`…`to`, both included. */
export const madridDateRangeBounds = (from: string, to: string): MadridInterval => {
  if (calendarDaysBetween(from, to) < 0) throw new MadridCalendarError('RANGE_INVALID')
  return Object.freeze({ from_inclusive: madridDayStart(from), until_exclusive: madridDayStart(addCalendarDays(to, 1)) })
}

/** One Madrid business day: its date, ISO weekday and instant bounds. */
export type MadridBusinessDay = Readonly<{
  business_date: string
  /** 1 = Monday … 7 = Sunday. */
  iso_weekday: number
  from_inclusive: string
  until_exclusive: string
}>

export const madridBusinessDay = (date: string): MadridBusinessDay =>
  Object.freeze({ business_date: date, iso_weekday: isoWeekdayOfDate(date), ...madridDayBounds(date) })

/** The `count` previous calendar dates on the same weekday as `date`, most recent first. */
export const previousSameWeekdays = (date: string, count: number): readonly string[] => {
  assertCalendarDate(date)
  if (!Number.isSafeInteger(count) || count < 1 || count > 12) throw new MadridCalendarError('RANGE_INVALID')
  return Object.freeze(Array.from({ length: count }, (_, index) => addCalendarDays(date, -7 * (index + 1))))
}

/** The `count` calendar dates ending at `date`, oldest first. */
export const trailingDates = (date: string, count: number): readonly string[] => {
  assertCalendarDate(date)
  if (!Number.isSafeInteger(count) || count < 1 || count > 60) throw new MadridCalendarError('RANGE_INVALID')
  return Object.freeze(Array.from({ length: count }, (_, index) => addCalendarDays(date, index - (count - 1))))
}

/** Weekday of a calendar date: 0 = Sunday … 6 = Saturday (the `weekday` of `MadridLocalTime`). */
export const weekdayOfDate = (date: string): number => new Date(`${assertCalendarDate(date)}T00:00:00.000Z`).getUTCDay()

/** The Monday of the Monday–Sunday week that contains `date` (the date itself on a Monday). */
export const isoWeekStart = (date: string): string => addCalendarDays(date, 1 - isoWeekdayOfDate(date))

/** The Sunday of the Monday–Sunday week that contains `date` (the date itself on a Sunday). */
export const isoWeekEnd = (date: string): string => addCalendarDays(isoWeekStart(date), 6)

/**
 * The Monday of the last whole Monday–Sunday week that has ended by the end of `date`: the week
 * ending that day when `date` is a Sunday, else the week before the one that contains `date`.
 */
export const closedIsoWeekStart = (date: string): string => {
  const isoWeekday = isoWeekdayOfDate(date)
  return addCalendarDays(isoWeekday === 7 ? date : addCalendarDays(date, -isoWeekday), -6)
}

const MINUTES_PER_DAY = 24 * 60
const CLOCK_TEXT = /^([01]\d|2[0-3]):([0-5]\d)$/

/**
 * `HH:MM` on a 24-hour clock of a whole number of minutes after local midnight. A value outside one
 * day wraps as a wall clock does (1440 → `00:00`, −30 → `23:30`).
 */
export const clockText = (minutes: number): string => {
  if (!Number.isSafeInteger(minutes)) throw new MadridCalendarError('RANGE_INVALID')
  const wrapped = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
  return `${pad(Math.floor(wrapped / 60))}:${pad(wrapped % 60)}`
}

/** True for a wall-clock `HH:MM` from `00:00` to `23:59`. */
export const isClockText = (value: unknown): value is string => typeof value === 'string' && CLOCK_TEXT.test(value)

/** Minutes after local midnight of a `HH:MM` wall clock (`00:00` → 0, `23:59` → 1439). */
export const clockMinutes = (clock: string): number => {
  const match = typeof clock === 'string' ? CLOCK_TEXT.exec(clock) : null
  if (!match) throw new MadridCalendarError('RANGE_INVALID')
  return Number(match[1]) * 60 + Number(match[2])
}

/** The Madrid wall clock `HH:MM` of an instant. */
export const madridClock = (value: string | Date): string => clockText(madridLocalTime(value).localMinutes)

/** Minutes the Madrid wall clock is ahead of UTC at an instant: 60 in winter (CET), 120 in summer (CEST). */
export const madridUtcOffsetMinutes = (value: string | Date): number => Math.round(offsetAt(instantOf(value).getTime()) / 60_000)
